import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { FiFlag } from "react-icons/fi";
import { FaCommentAlt, FaWhatsapp } from "react-icons/fa";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import "./FloatingSupport.css";

type Props = {
  signedIn: boolean;
  whatsappUrl: string;
  title: string;
  whatsappLabel: string;
  reportLabel: string;
  closeLabel: string;
  onReport: () => void;
  children?: ReactNode;
};

export default function FloatingSupport({
  signedIn,
  whatsappUrl,
  title,
  whatsappLabel,
  reportLabel,
  closeLabel,
  onReport,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const [layer] = useState(() => document.createElement("div"));
  const controls = useRef<HTMLDivElement>(null);
  const restoreFocus = () =>
    controls.current
      ?.querySelector<HTMLButtonElement>(".ds-support__trigger")
      ?.focus();

  useEffect(() => {
    const element = layer;
    element.className = "ds-support-layer";
    document.body.appendChild(element);
    // The browser top layer sits above legacy overlays regardless of z-index.
    element?.setAttribute("popover", "manual");
    const popover = element as HTMLDivElement & {
      showPopover?: () => void;
      hidePopover?: () => void;
    };
    popover?.showPopover?.();
    // Modal libraries hide body siblings from assistive technology. Support
    // remains an interactive top layer, so keep this one sibling accessible.
    const exposeSupport = () => {
      if (element?.getAttribute("aria-hidden") === "true")
        element.removeAttribute("aria-hidden");
    };
    exposeSupport();
    const observer = new MutationObserver(exposeSupport);
    if (element)
      observer.observe(element, {
        attributes: true,
        attributeFilter: ["aria-hidden"],
      });
    // Underlying MUI/Bootstrap dialogs must not pull focus out of support.
    const keepFocus = (event: Event) => event.stopPropagation();
    element?.addEventListener("focusin", keepFocus);
    return () => {
      observer.disconnect();
      element?.removeEventListener("focusin", keepFocus);
      popover?.hidePopover?.();
      element.remove();
    };
  }, [layer]);

  // While a side drawer (design Drawer) is open, support goes behind it:
  // out of the top layer and just below the drawer's z-index, so it doesn't
  // cover the drawer's footer buttons. Back on top when the drawer closes.
  useEffect(() => {
    const element = layer as HTMLDivElement & {
      showPopover?: () => void;
      hidePopover?: () => void;
    };
    const update = () => {
      const drawerOpen = Boolean(document.querySelector("body > .ds-drawer"));
      const behind = element.classList.contains("ds-support-layer--behind");
      if (drawerOpen === behind) return;
      element.classList.toggle("ds-support-layer--behind", drawerOpen);
      try {
        if (drawerOpen) element.hidePopover?.();
        else element.showPopover?.();
      } catch {
        // already in the requested state
      }
    };
    update();
    // drawers are portalled straight into <body>
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [layer]);

  useEffect(() => {
    if (!open) return undefined;
    controls.current
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
      ?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!controls.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
      restoreFocus();
    };
    document.addEventListener("pointerdown", dismiss, true);
    window.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("pointerdown", dismiss, true);
      window.removeEventListener("keydown", escape, true);
    };
  }, [open]);

  return createPortal(
    <>
      {children}
      <div
        ref={controls}
        className="ds-support"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node))
            setOpen(false);
        }}
      >
        {signedIn ? (
          <>
            {open && (
              <div
                id="support-options"
                className="ds-support__options"
                role="menu"
                aria-label={title}
                onKeyDown={(event) => {
                  const items = Array.from(
                    event.currentTarget.querySelectorAll<HTMLButtonElement>(
                      '[role="menuitem"]'
                    )
                  );
                  const index = items.indexOf(
                    document.activeElement as HTMLButtonElement
                  );
                  let next: number;
                  if (event.key === "ArrowDown")
                    next = (index + 1) % items.length;
                  else if (event.key === "ArrowUp")
                    next = (index - 1 + items.length) % items.length;
                  else if (event.key === "Home") next = 0;
                  else if (event.key === "End") next = items.length - 1;
                  else return;
                  event.preventDefault();
                  event.stopPropagation();
                  items[next]?.focus();
                }}
              >
                <Button
                  variant="ghost"
                  className="ds-support__action ds-support__action--report"
                  role="menuitem"
                  aria-label={reportLabel}
                  onClick={() => {
                    setOpen(false);
                    onReport();
                  }}
                >
                  <span className="ds-support__label">{reportLabel}</span>
                  <span className="ds-support__action-icon">
                    <FiFlag aria-hidden="true" />
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  className="ds-support__action ds-support__action--whatsapp"
                  role="menuitem"
                  aria-label={whatsappLabel}
                  onClick={() => {
                    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
                    setOpen(false);
                    restoreFocus();
                  }}
                >
                  <span className="ds-support__label">{whatsappLabel}</span>
                  <span className="ds-support__action-icon">
                    <FaWhatsapp aria-hidden="true" />
                  </span>
                </Button>
              </div>
            )}
            <Button
              className="ds-support__trigger"
              size="lg"
              iconOnly
              title={open ? closeLabel : title}
              aria-label={open ? closeLabel : title}
              aria-expanded={open}
              aria-haspopup="menu"
              aria-controls={open ? "support-options" : undefined}
              iconStart={
                open ? (
                  <Icon name="close" size={24} />
                ) : (
                  <FaCommentAlt aria-hidden="true" />
                )
              }
              onClick={() => setOpen(!open)}
              onKeyDown={(event) => {
                if (["ArrowUp", "ArrowDown"].includes(event.key)) {
                  event.preventDefault();
                  setOpen(true);
                }
              }}
            />
          </>
        ) : (
          <a
            className="ds-support__whatsapp"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={whatsappLabel}
          >
            <FaWhatsapp aria-hidden="true" />
          </a>
        )}
      </div>
    </>,
    layer
  );
}
